<template>
  <div ref="wrapper" class="chromatogram-single chromatogram">
    <div>
      <GlobalEvents @click="handleClickOutsideChart" />

      <div style="position: relative; width: 100%" @click.stop>
        <LoadingComponent
          v-show="updated !== true"
          :label="null"
          style="position: absolute; width: 16px; margin: 0; z-index: 100; right: 32px; top: 24px"
        />
        <div ref="divChart" class="chromatogram-chart" />

        <Btn
          v-if="hasData && toolbar && !isShowEditPanel"
          padding="xl"
          class="chromatogram__btn-show-edit-panel"
          @click="showEditPanel"
        >
          <IconMaterial title="build" class="chromatogram__icon-build" />
        </Btn>
        <Prompt
          v-if="isShowPrompt"
          :message="helpMessage"
          class="chromatogram-single__prompt"
          @hide="hidePrompt"
        />
      </div>

      <div v-if="toolbar && isShowEditPanel" class="chromatogram-single__wrapper-edit-panel">
        <ChromatogramEdit
          :id="id"
          v-model="mode"
          :hasDetectionTime="hasDetectionTime"
          :isEditable="editable"
          :canCompare="!this.sampleToken"
          class="chromatogram-single__edit-panel"
          :hasPostprocessing="postprocessing != null"
          :measurements="[internalMeasurement]"
          :sampleId="injection.sample_id"
          :hasData="hasData"
          :isShared="isShared"
          @zoomToDetectionTime="zoomToDetectionTime"
          @updateAppearance="updateAppearance"
          @applyPostprocessingTemplate="applyPostprocessingTemplate"
          @editPostprocessing="editPostprocessing"
          @resetPostprocessing="resetPostprocessing"
          @close="hideEditPanel"
        />
      </div>

      <template v-if="hasData">
        <PrTableSamplePeaks
          v-if="showPeaksList"
          :editable="editable"
          :ppeaks="peaks"
          :measurement-socket="measurementSocket"
          :highlightedPeakId="highlightedPeakId"
          :focusedCompoundInputId.sync="focusedCompoundInputId"
          :canRemove="canRemovePeak"
          @selection="setPeakHighlighting"
        />
        <slot
          name="peaks"
          :peaks="peaks"
          :measurementSocket="measurementSocket"
          :setPeakHighlighting="setPeakHighlighting"
          :highlightedPeakId="highlightedPeakId"
          style="margin-top: 16px"
        />
      </template>
    </div>

    <!--Absolute positioned-->
    <PostprocessingTemplateEditModal
      :postprocessingTemplate="postprocessingForEdit"
      :selectedMeasurement="measurement"
      :method="method"
      :isApplyMode="true"
    />
  </div>
</template>

<script>
  import resize from 'vue-resize-directive';
  import MeasurementSocket from 'api/sockets/MeasurementSocket';
  import { apiMeasurements } from '@/api/graphql/cloud/measurements';

  import LoadingComponent from 'components/element/LoadingComponent';
  import PrTableSamplePeaks from 'components/blocks/charts/chromatogramNew/private/PrTableSamplePeaks.vue';

  import {
    // CHART_COLORS,
    UNKNOWN_VALUE,
  } from 'utils/chart/chart-constants.ts';

  import ChromatogramEdit from '@/uikitProject/chromatogram/panels/vueChromatogramEdit/ChromatogramEdit';
  import Btn from '@/uikitBase/btns/Btn';
  import IconMaterial from '@/uikitBase/icons/IconMaterial';
  import Prompt from '@/uikitProject/chromatogram/help/Prompt';
  import { consoleHelpers } from 'utils/logHelpers';
  import { ChromatogramDrawer, Modes } from '@/uikitProject/charts/new/ChromatogramDrawer';
  import { isInjectionInProgress } from '@/utils/injectionHelpers';
  import { persistentStorage, PersistentStorageKeys } from '@/utils/persistentStorage';
  import PostprocessingTemplateEditModal from '@/components/block/modal/PostprocessingTemplateEditModal.vue';
  import { mergeDataObjects } from '@/utils/measurementHelpers';
  import { getPostprocessingFlat } from '@/utils/postprocessingHelpers';
  import { sendDebugEvent } from '@/utils/sentryHelpers';

  const EVENT_ZOOM = 'zoom';
  const EVENT_UPDATE = 'update';
  const EVENT_CREATED = 'created';
  const EVENT_MOUNTED = 'mounted';
  const EVENT_DESTROY = 'destroy';
  const EVENT_ZOOM_ACTIVATION = 'zoomActivation';
  const EVENT_LIVE_UPDATE = 'liveUpdate';

  const MODE_ZOOM = 'zoom';
  const MODE_PEAK_ADD = 'peakAdd';
  const MODE_PEAK_ADD_MAGNET = 'peakAddMagnet';
  const MODE_PEAK_DELETE = 'peakDelete';

  const MESSAGE_APPLYING_PP = 'Applying postprocessing';

  export default {
    name: 'PrChartChromatogramSingle',

    components: {
      PostprocessingTemplateEditModal,
      Prompt,
      IconMaterial,
      Btn,
      ChromatogramEdit,
      LoadingComponent,
      PrTableSamplePeaks,
    },

    directives: { resize },

    props: {
      measurement: Object,
      canSelectPeak: { type: Boolean, default: false },
      editable: { type: Boolean, default: true },
      sampleToken: { type: String, default: null },
      // colorScheme: { type: Object, default: () => CHART_COLORS },
      toolbar: { type: Boolean, default: true },
      showPeaksList: { type: Boolean, default: false },
      // Chart shouldn't be smaller than bounds
      bounds: { type: Object, default: null },
      // heightPx: { type: Number, default: 300 },
      // hasPadding: { type: Boolean, default: true },
      hasLiveUpdate: { type: Boolean, default: true },
      timeMin: {
        type: Number,
        default: 0,
      },
      timeMax: {
        type: Number,
      },
      hasOnlyHorizontalZoom: {
        type: Boolean,
      },

      isApplyPostprocessing: {
        type: Boolean,
      },

      creationDate: {
        type: Date,
      },

      injection: {
        type: Object,
      },
      method: {
        type: Object,
      },
      width: {
        type: Number,
      },

      initialZoom: {
        type: Object,
      },

      postprocessing: {
        type: Object,
      },

      hasZoomActivationOverlay: {
        type: Boolean,
        required: true,
      },

      hasPostprocessingsToApply: {
        type: Boolean,
        required: true,
      },

      doRefreshPeaks: {
        type: Boolean,
        required: false,
      },
      doReapplyPostprocessing: {
        type: Boolean,
        default: true,
      },

      canRemovePeak: {
        type: Boolean,
      },
    },

    data() {
      return {
        mode: MODE_ZOOM, // 0 - zoom, 1 - add peak, 2 - draw baseline, 3 - delete peak
        internalMeasurement: this.measurement?.data ? this.measurement : {},
        measurementSocket: null,
        updated: this.hasLiveUpdate ? null : true,

        highlightedPeakId: null,
        focusedCompoundInputId: null,

        isShowEditPanel: true,
        isShowPrompt: false,

        listenersGroupId: null,

        drawer: null,

        baselinePoints: null,

        onBaselineUpdateCallback: null,

        isFetchingData: true,

        isInjectionInProgress: false,

        postprocessingRefreshIntervalId: null,

        messageOverlay: null,

        subscriptionForMeasurementsData: null,
        subscriptionForMeasurement: null,
      };
    },

    computed: {
      empty() {
        return this.internalMeasurement?.data_length <= 0;
      },
      peaks() {
        return this.internalMeasurement?.peaks;
      },
      minutesMax() {
        return this.privateTimeMax;
      },
      modeZoom: {
        get() {
          return this.mode === MODE_ZOOM;
        },
        set(b) {
          if (b) this.mode = MODE_ZOOM;
        },
      },
      modePeakAdd: {
        get() {
          return this.mode === MODE_PEAK_ADD || this.mode === MODE_PEAK_ADD_MAGNET;
        },
        set(b) {
          this.mode = b ? MODE_PEAK_ADD : MODE_ZOOM;
        },
      },
      modePeakAddMagnet: {
        get() {
          return this.mode === MODE_PEAK_ADD_MAGNET;
        },
        set(b) {
          this.mode = b ? MODE_PEAK_ADD_MAGNET : MODE_ZOOM;
        },
      },
      modePeakDelete: {
        get() {
          return this.mode === MODE_PEAK_DELETE;
        },
        set(b) {
          this.mode = b ? MODE_PEAK_DELETE : MODE_ZOOM;
        },
      },
      id() {
        return this.internalMeasurement?.id;
      },
      baseline() {
        const b = this.internalMeasurement?.baseline;
        return b && b.length ? b : null;
      },
      mps() {
        return this.internalMeasurement?.mps;
      },
      data() {
        return this.internalMeasurement?.data;
      },
      helpMessage() {
        switch (this.mode) {
          case MODE_PEAK_DELETE:
            return 'Click and hold to remove all peaks inside';
          // TODO remove magnet mode
          case MODE_PEAK_ADD:
          case MODE_PEAK_ADD_MAGNET:
            return 'Click and hold to draw a new peak';
          default:
            return null;
        }
      },

      detectionTime() {
        const dtimPostprocessing = this.postprocessing?.postprocessings?.DTIM;
        const injectionDurationMin = this.expectedDurationSeconds / 60;

        return dtimPostprocessing
          ? {
              start: dtimPostprocessing?.detection_time_start_sec
                ? dtimPostprocessing.detection_time_start_sec / 60
                : null,
              end: dtimPostprocessing?.detection_time_end_sec
                ? Math.min(dtimPostprocessing.detection_time_end_sec / 60, injectionDurationMin)
                : null,
            }
          : null;
      },

      hasDetectionTime() {
        return Boolean(
          this.detectionTime &&
            (this.detectionTime.start ||
              (this.detectionTime.end &&
                this.detectionTime.end !==
                  (this.method?.run_time_min ?? this.injection.expected_duration_sec / 60))),
        );
      },
      privateTimeMin() {
        return this.timeMin;
      },
      privateTimeMax() {
        return this.timeMax;
      },
      expectedDurationSeconds() {
        if (this.timeMax != null && Number.isFinite(this.timeMax)) {
          return this.timeMax * 60;
        }
        if (this.method?.run_time_min != null && Number.isFinite(this.method.run_time_min)) {
          return this.method.run_time_min * 60;
        }
        if (this.injection?.expected_duration_sec != null) {
          return this.injection.expected_duration_sec;
        }
        if (this.injection?.expected_duration_ms != null) {
          return this.injection.expected_duration_ms / 1000;
        }
        return 0;
      },

      hasData() {
        return this.data?.length > 1;
      },

      wasPostprocessed() {
        return this.postprocessing?.measurement.processing_state !== 'error' &&
          this.postprocessing?.postprocessings
          ? Object.values(this.postprocessing?.postprocessings).length > 0
          : false;
      },

      isShowBaseline() {
        return !this.isApplyPostprocessing && this.wasPostprocessed;
      },
      isShowPeaks() {
        return (this.wasPostprocessed && this.isApplyPostprocessing) || !this.wasPostprocessed;
      },

      isShared() {
        return this.sampleToken != null;
      },

      postprocessingForEdit() {
        return this.postprocessing
          ? {
              name: '',
              ...getPostprocessingFlat(this.postprocessing.postprocessings),
            }
          : undefined;
      },
    },

    watch: {
      updated(value) {
        consoleHelpers.warn('UPDATED', value);
      },
      data: 'refresh',
      bounds: 'refresh',
      detectionTime: 'refresh',
      width(value) {
        this.drawer?.actions.setWidth(value);
      },
      privateTimeMin() {
        this.refresh();
      },
      privateTimeMax() {
        this.refresh();
      },
      measurement(m, oldM) {
        if (m === this.internalMeasurement) return;

        console.warn('from measurement watcher');
        const idChanged = oldM?.id !== m?.id;

        if (!idChanged && m.data == null) return;

        this.internalMeasurement = m;

        if (idChanged) {
          this.updated = false;
          this.destroySocket();
          this.stopSubscriptions();

          if (m?.id) {
            console.warn('watch initMeasurement');

            this.initMeasurement(m.ulid);
            this.measurementSocket = this.createSocket(m.id);
          }
        }
      },
      mode() {
        this.isShowPrompt = Boolean(this.helpMessage);

        switch (this.mode) {
          case MODE_ZOOM: {
            this.drawer.setMode(Modes.DEFAULT);
            break;
          }
          case MODE_PEAK_ADD: {
            this.onEditPeaks();
            this.drawer.setMode(Modes.ADD_PEAK);
            break;
          }
          case MODE_PEAK_DELETE: {
            this.onEditPeaks();
            this.drawer.setMode(Modes.REMOVE_PEAK);
            break;
          }
        }
      },
      isApplyPostprocessing() {
        this.refreshIsApplyPostprocessing();
      },
      isFetchingData(value) {
        this.drawer?.actions.setIsFetchingData(value);
      },
      messageOverlay(value) {
        console.warn('set messageOverlay', value);
        this.drawer?.actions.setOverlayMessage(value);
      },
      'injection.state': {
        handler() {
          this.isInjectionInProgress = isInjectionInProgress(this.injection);

          this.drawer?.actions.setIsInProgress(this.isInjectionInProgress);
        },
        immediate: true,
      },
      postprocessing: {
        handler() {
          this.internalMeasurement = {
            ...this.internalMeasurement,
            postprocessings: this.postprocessing
              ? Object.values(this.postprocessing.postprocessings)
              : [],
          };
        },
        immediate: true,
      },
      isInjectionInProgress(isInProgress) {
        isInProgress
          ? this.subscriptionForMeasurementsData.resume()
          : this.subscriptionForMeasurementsData.pause();
      },
    },

    created() {
      this.measurementSocket = this.createSocket(this.measurement?.id);

      this.$emit(EVENT_CREATED);

      if (this.measurement?.ulid != null) {
        console.warn('created initMeasurement');
        this.initMeasurement(this.measurement.ulid);
      }
    },

    mounted() {
      this.initChart();
      this.$emit(EVENT_MOUNTED);
    },

    beforeDestroy() {
      this.destroySocket();
      this.stopSubscriptions();
      this.$emit(EVENT_DESTROY);
    },

    methods: {
      initChart() {
        if (!this.internalMeasurement?.id || !this.internalMeasurement?.data) {
          return;
        }

        if (this.$refs.wrapper == null) {
          sendDebugEvent('#2413: Front: Fix sentry issues', [
            ['measurementId', this.internalMeasurement?.id],
            [
              'Sentry issue',
              'https://newcrom.sentry.io/issues/5618065224/?project=6192768&query=is:unresolved+issue.priority:%5Bhigh,+medium%5D&statsPeriod=30d&stream_index=3&utc=true',
            ],
          ]);

          return;
        }

        const { hasGridLines, hasTooltipOnHover } = this.getAppearance();

        console.warn('initChart', this.internalMeasurement);

        this.drawer = new ChromatogramDrawer({
          container: this.$refs.divChart,
          measurements: [this.internalMeasurement],
          expectedDurationSeconds: this.expectedDurationSeconds,
          detectionTime: this.detectionTime
            ? {
                start: this.detectionTime.start,
                end: this.detectionTime.end,
              }
            : null,
          isInProgress: isInjectionInProgress(this.injection),
          isShowBaseline: this.isShowBaseline,
          isShowPeaks: this.isShowPeaks,
          isFetchingData: this.isFetchingData,
          widthPx: this.$refs.wrapper.getBoundingClientRect().width,
          hasGridLines,
          hasTooltipOnHover,
          zoomBounds: this.initialZoom,
          hasZoomActivationOverlay: this.hasZoomActivationOverlay,
          creationData: this.creationDate,
        });

        this.drawer.on('addPeak', ({ startMau, endMau, startMinutes, endMinutes }) => {
          const start = { mau: startMau, time: startMinutes };
          const end = { mau: endMau, time: endMinutes };
          this.peakAdd(start, end);
          this.modeZoom = true;
        });

        this.drawer.on('deletePeaks', (peaksForDelete) => {
          this.removePeaks(peaksForDelete);
          this.modeZoom = true;
        });

        this.drawer.on('zoom', (zoomBounds) => {
          console.warn('event zoom', zoomBounds);
          this.$emit(EVENT_ZOOM, zoomBounds);
        });

        this.drawer.on('highlightPeak', (peakId) => {
          this.highlightedPeakId = peakId;
        });

        this.drawer.on('zoomActivation', () => {
          this.$emit(EVENT_ZOOM_ACTIVATION);
        });
      },

      refresh() {
        if (this.internalMeasurement?.data == null) {
          return;
        }

        if (this.drawer) {
          this.drawer.actions.setIsInProgress(isInjectionInProgress(this.injection));
          this.drawer.actions.setDetectionTime(
            this.detectionTime
              ? {
                  start: this.detectionTime.start,
                  end: this.detectionTime.end,
                }
              : null,
          );
          this.drawer.actions.setExpectedDurationSeconds(this.expectedDurationSeconds);
          this.drawer.refresh([this.internalMeasurement]);
        } else {
          this.initChart();
        }
      },

      showEditPanel() {
        this.isShowEditPanel = true;
      },
      hideEditPanel() {
        this.isShowEditPanel = false;
      },
      hidePrompt() {
        this.isShowPrompt = false;
      },

      zoomToDetectionTime() {
        this.drawer.actions.zoomToDetectionTime();
      },

      setPeakHighlighting({ peakId, isSelected }) {
        this.drawer?.actions.setPeakHighlighting(peakId, isSelected);
      },

      setZoom(zoomBounds) {
        this.chart.actions.setZoom(zoomBounds);
      },

      async onMeasurement(m) {
        const _data = await m.getData(
          this.isApplyPostprocessing && this.wasPostprocessed && !this.isInjectionInProgress,
        );

        const data = this.internalMeasurement?.data
          ? _data?.length >= this.internalMeasurement.data?.length
            ? _data
            : this.internalMeasurement.data
          : _data;

        this.internalMeasurement = {
          ...this.internalMeasurement,
          ...m,
          data,
          data_length: data.length,
        };

        this.updated = true;

        this.drawer?.actions.setIsShowBaseline(this.isShowBaseline);
        this.drawer?.actions.setIsShowPeaks(this.isShowPeaks);

        this.refresh();
      },

      createSocket(id) {
        if (this.isShared) {
          return;
        }

        if (!id) {
          throw new Error(`Can't establish a socket connection without id`);
        }

        return MeasurementSocket.start(id);
      },

      handleClickOutsideChart() {
        // No actions
      },

      resetZoom() {
        console.warn('RESET ZOOM');
      },

      // socket methods
      destroySocket() {
        if (!this.hasLiveUpdate) {
          return;
        }

        if (this.measurementSocket) {
          this.measurementSocket.close(this.listenersGroupId);
        }
      },

      peakAdd(start, end) {
        this.showNotificationIfRpcError(async () => {
          const peakData = {
            measurementId: this.measurement.id,
            startPoint: {
              time: start.time,
              mau: start.mau,
            },
            endPoint: {
              time: end.time,
              mau: end.mau,
            },
          };
          await apiMeasurements.createManualPeak(peakData);
        });
        this.$emit(EVENT_UPDATE);
      },
      removePeaks(ids) {
        this.showNotificationIfRpcError(() => apiMeasurements.deletePeaks(ids));
        this.$emit(EVENT_UPDATE);
      },

      getAppearance() {
        return (
          persistentStorage.get(PersistentStorageKeys.CHROMATOGRAM_APPEARANCE) ?? {
            hasGridLines: false,
            hasTooltipOnHover: false,
          }
        );
      },

      updateAppearance(appearance) {
        this.drawer?.actions.setHasGridLines(appearance.hasGridLines);
        this.drawer?.actions.setHasTooltipOnHover(appearance.hasTooltipOnHover);
      },

      applyPostprocessingTemplate() {
        this.$modal.show('postprocessing-template-edit');
      },
      editPostprocessing() {
        this.$modal.show('postprocessing-template-edit');
      },
      resetPostprocessing() {
        this.$modal.show('dialog', {
          title: 'Reset postprocessing',
          text: `Current data (peaks, baseline, noise reduction) will be removed`,
          buttons: [
            {
              title: 'Confirm',
              handler: async () => {
                try {
                  await apiMeasurements.resetPostprocessing(this.measurement.id);
                } catch (e) {
                  this.notifyError(`Can't reset postprocessing`);
                  return;
                }

                this.$emit('applyPostprocessing', false);
                this.$modal.hide('dialog');
              },
              class: 'vue-dialog-button blue-text',
            },
            {
              title: 'Cancel',
              default: true,
              class: 'vue-dialog-button red-text',
            },
          ],
        });
      },

      onEditPeaks() {
        if (this.postprocessing && !this.isApplyPostprocessing) {
          this.$emit('applyPostprocessing', true);
          this.notify('You can only edit peaks on post-processed data if it exists', 10);
        }
      },

      async refreshIsApplyPostprocessing() {
        if (!this.internalMeasurement) {
          return;
        }

        const _data = (await this.internalMeasurement.getData?.(this.isApplyPostprocessing)) ?? [];
        console.warn('data from CDN', _data);

        // const data =
        //   (await this.internalMeasurement.getData?.(this.isApplyPostprocessing)) ??
        //   this.internalMeasurement.data ??
        //   [];

        const data =
          _data.length >= this.internalMeasurement.data?.length
            ? _data
            : this.internalMeasurement.data ?? [];

        console.warn('from refreshIsApplyPostprocessing');

        this.internalMeasurement = {
          ...this.internalMeasurement,
          data,
          data_length: data.length,
        };

        console.warn(
          'refreshIsApplyPostprocessing this.internalMeasurement =',
          this.internalMeasurement,
        );

        this.drawer?.actions.setIsShowBaseline(this.isShowBaseline);
        this.drawer?.actions.setIsShowPeaks(this.isShowPeaks);

        this.refresh();
      },

      initMeasurement(measurementUlid) {
        // TODO check isFetchingData
        // this.isFetchingData = true;

        console.warn('initMeasurement', measurementUlid);
        this.stopSubscriptions();

        if (this.hasLiveUpdate && isInjectionInProgress(this.injection)) {
          this.$emit(EVENT_LIVE_UPDATE);
          this.initSubscriptionForMeasurementData(measurementUlid);
        }
        this.initSubscriptionForMeasurement(this.measurement.id);
      },
      stopSubscriptions() {
        console.warn(
          'stopSubscriptions',
          this.injection.id,
          this.subscriptionForMeasurementsData,
          this.subscriptionForMeasurement,
        );
        this.subscriptionForMeasurementsData?.unsubscribe();
        this.subscriptionForMeasurement?.unsubscribe();
      },

      initSubscriptionForMeasurementData(measurementUlid) {
        const onNewData = (from, values) => {
          const currentData = this.internalMeasurement?.data;
          const currentLength = currentData?.length ?? 0;

          if (currentLength === from) {
            const data = [...(currentData ?? []), ...values];
            this.internalMeasurement = {
              ...this.internalMeasurement,
              data,
              data_length: data.length,
            };
          } else if (currentLength < from) {
            const difference = from - currentLength;
            const unknownValues = Array(difference).fill(UNKNOWN_VALUE);
            const data = [...(currentData ?? []), ...unknownValues, ...values];

            this.internalMeasurement = {
              ...this.internalMeasurement,
              data,
              data_length: data.length,
            };
          } else {
            const data = [...(currentData ?? [])];
            data.splice(from, values.length, ...values);

            this.internalMeasurement = {
              ...this.internalMeasurement,
              data,
              data_length: data.length,
            };
          }
        };

        this.subscriptionForMeasurementsData = apiMeasurements.subscribeForMeasurementsData(
          measurementUlid,
          (data) => {
            console.warn('from subscriptionForMeasurement data');

            if (data?.length !== 0) {
              onNewData(data[0].from, data.length === 1 ? data[0].values : mergeDataObjects(data));
            }
          },
          this.sampleToken,
          this.injection.sample_id,
          { isPaused: !this.isInjectionInProgress },
        );
      },

      initSubscriptionForMeasurement(measurementId) {
        this.subscriptionForMeasurement = apiMeasurements.subscribeForMeasurement(
          measurementId,
          (measurement) => {
            this.isFetchingData = false;

            console.warn('from subscriptionForMeasurement');
            this.onMeasurement(measurement);

            this.messageOverlay =
              measurement.processing_state === 'in_progress' ? MESSAGE_APPLYING_PP : null;
          },
          this.sampleToken,
          this.injection.sample_id,
        );
      },
    },
  };
</script>

<style lang="scss" scoped>
  .chromatogram-single {
    &__wrapper-edit-panel {
      display: flex;
      justify-content: center;
      margin: 10px 0;
      padding-top: 16px;

      @media print {
        display: none;
      }

      @media (max-width: $screen-xs-max) {
        overflow-x: auto;
        justify-content: flex-start;

        &::before,
        &::after {
          content: '';
          display: block;
          width: 16px;
          flex: none;
        }
      }
    }

    &__prompt {
      position: absolute;
      top: 30px;
      right: 40px;
      z-index: 5;
    }
  }
</style>

<style lang="scss">
  .chromatogram {
    padding: 0;
    margin: 0 0 0 0;

    &__btn-show-edit-panel {
      position: absolute;
      top: 20px;
      right: 40px;
    }

    &__icon-build {
      color: $color-text-primary;
    }
  }

  .chromatogram-chart {
    position: relative;
    width: 100%;
    margin: 0;
    height: 300px;
  }
</style>
